import React from 'react';

import { cn } from '@/services/utils';

export type DialogBodyPadding = 'default' | 'none';

interface DialogBodyProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * When true, the body fills the remaining vertical space inside `DialogContent`
     * and scrolls internally. Enable for fixed-height dialogs (`variant="screen"`)
     * or any dialog whose content can exceed the viewport. Defaults to `false`,
     * which makes the body size to its natural content height (matches the
     * `variant="small"` / auto-height `variant="large"` behavior).
     */
    scrollable?: boolean;
    /**
     * Body padding token.
     * - `default` (BYRD spec): `tw-py-6 tw-px-5 lg:tw-px-8`.
     * - `none`: caller owns padding (e.g. bodies that embed their own toolbar
     *   or stepper header just below `DialogHeader`).
     */
    padding?: DialogBodyPadding;
}

const paddingMap: Record<DialogBodyPadding, string> = {
    default: 'tw-py-6 tw-px-5 lg:tw-px-8',
    none: '',
};

const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
    ({ className, scrollable = false, padding = 'default', ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'scoped-dialog-body',
                'tw-h-full',
                scrollable && 'tw-flex-1 tw-min-h-0 tw-overflow-y-auto',
                paddingMap[padding],
                className,
            )}
            {...props}
        />
    ),
);

DialogBody.displayName = 'DialogBody';

export { DialogBody };
